import { BrandProvider, LinkButton } from "@certn/cascadia-react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Trans, useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";

import { ConfigurationError } from "../components/ConfigurationError";
import { FullScreenErrorPage } from "../components/FullScreenErrorPage";
import { NoAccessOrSignUpRegion } from "../components/NoAccessToSignUpRegion";
import { ProxyProvider } from "../components/ProxyProvider";
import { PermissionsProvider } from "../components/permissions/PermissionsProvider";
import { UserFeaturesProvider } from "../components/user-feature/UserFeatureProvider";
import { useUser } from "../hooks/useUser";
import { currentRegion, regions } from "../utils/regionSignUp";
import {
  isCustomerAccountActive,
  isCustomerAccountVerifiedOrInGracePeriod,
} from "./utils";
import { useFetchCobranding } from "./settings/branding/colours/hooks/useFetchCobranding";

export function BasePage() {
  const { details: userDetails } = useUser();
  // Only fetch cobranding when the feature flag is enabled

  const customerGroups = userDetails?.customer_groups || [];
  const {
    supportExactlyOneGroupPerAccount,
    clientPortalShowNewRegionSignUp,
    clientPortalBrandNewSignUp,
    clientPortalBlockAccessIfNotValidated,
    enableFpCoBrandingV2,
  } = useFlags();

  // Only fetch cobranding when the feature flag is enabled
  const { primaryColor } = useFetchCobranding({
    enabled: enableFpCoBrandingV2,
  });

  const { t } = useTranslation();
  const regionName = regions[currentRegion];

  if (!userDetails) {
    if (clientPortalBrandNewSignUp) {
      return (
        <NoAccessOrSignUpRegion
          message={t("Welcome to Certn. Click the button below to sign up.")}
          title={t("Welcome")}
          buttonText={t("Sign up to new region")}
          icon="sparkles"
        />
      );
    }

    return (
      <ConfigurationError
        message={t(
          "It looks like something has gone wrong on our side, please refresh or try again later."
        )}
      />
    );
  }

  // A person who has an existing client account but in a different region
  if (!userDetails.client_account?.auth0_id) {
    if (clientPortalShowNewRegionSignUp) {
      return (
        <NoAccessOrSignUpRegion
          message={t(
            "You've not used this region before, do you want to sign-up in {{regionName}}?`",
            { regionName }
          )}
          title={t("No Access")}
          buttonText={t("Sign up to new region")}
          icon="sparkles"
        />
      );
    }
    return (
      <ConfigurationError
        message={t(
          "It looks like something has gone wrong on our side, please refresh or try again later."
        )}
      />
    );
  }

  if (clientPortalBlockAccessIfNotValidated) {
    if (
      !isCustomerAccountActive(userDetails) ||
      !isCustomerAccountVerifiedOrInGracePeriod(userDetails)
    ) {
      return (
        <FullScreenErrorPage
          title={t("Account blocked")}
          message={
            <Trans>
              Your customer account has been blocked. Please{" "}
              <LinkButton
                testID="customer-account-blocked-contact-support"
                linkStyle="monochrome"
                textStyle="global/body-secondary"
                onPress={() => {
                  if (typeof window.zE === "function") {
                    window.zE("messenger", "open");
                  } else {
                    window.location.href = "https://help.certn.co/";
                  }
                }}
              >
                contact support
              </LinkButton>
              .
            </Trans>
          }
          illustration="verification-fail"
        />
      );
    }
  }

  if (supportExactlyOneGroupPerAccount && !customerGroups.length) {
    return (
      <ConfigurationError
        message={t(
          "Your user doesn't have access to this page. Please contact support."
        )}
        title={t("Error")}
      />
    );
  }

  return (
    <PermissionsProvider>
      <ProxyProvider>
        <UserFeaturesProvider>
          {enableFpCoBrandingV2 ? (
            <BrandProvider primaryColor={primaryColor || undefined}>
              <Outlet />
            </BrandProvider>
          ) : (
            <Outlet />
          )}
        </UserFeaturesProvider>
      </ProxyProvider>
    </PermissionsProvider>
  );
}
